import {Dispatch, SetStateAction, useState} from "react";
import {toast} from "react-toastify";
import styled from "styled-components";
import {scheduleApi} from "../../api/schedule-api";
import {useAppDispatch} from "../../app/hooks";
import {setSchedulePageAllByDtoAction} from "../../app/reducers/schedule-slice";
import theme from "../../style/theme";
import {Schedule} from "../../types/schedule/schedule";

const SCHEDULE_ALIAS_PLACEHOLDER = "라인명을 입력하세요";

interface Props {
    displaySchedule: Schedule;
    setUpdateScheduleAliasDialogOpen: Dispatch<SetStateAction<boolean>>;
    ref?: React.RefObject<HTMLDivElement>;
}

export const UpdateScheduleAliasDialog: React.FC<Props> = (
    {
        displaySchedule,
        setUpdateScheduleAliasDialogOpen,
        ref,
    }
) => {
    const dispatch = useAppDispatch();
    const [newScheduleAlias, setNewScheduleAlias] = useState(displaySchedule.alias);

    const handleChangeTextField = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setNewScheduleAlias(event.target.value);
    };

    const handleStopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    const clearPlaceholder = (event: React.FocusEvent<HTMLInputElement>) => {
        event.target.placeholder = "";
    };

    const resetPlaceholder =
        (placeholder: string) => (event: React.FocusEvent<HTMLInputElement>) => {
            event.target.placeholder = placeholder;
        };

    const handleClickUpdateScheduleAliasButton = async () => {
        if (!newScheduleAlias) {
            return;
        }
        if (newScheduleAlias === displaySchedule.alias) {
            setUpdateScheduleAliasDialogOpen(false);
            return;
        }
        try {
            await scheduleApi.updateAlias(displaySchedule.id, {
                alias:
                    newScheduleAlias.length < 18
                        ? newScheduleAlias
                        : newScheduleAlias.slice(0, 17),
            });
            const response = await scheduleApi.getById(displaySchedule.id);
            dispatch(setSchedulePageAllByDtoAction({scheduleDto: response.data}));
            toast.success("라인명이 변경되었습니다.");
            setUpdateScheduleAliasDialogOpen(false);
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <UpdateScheduleAliasContainer ref={ref} onClick={handleStopPropagation}>
            <UpdateScheduleAliasTitle>라인명 수정</UpdateScheduleAliasTitle>
            <AuthTextFieldContainer>
                <RightText>{`${newScheduleAlias?.length}/17`}</RightText>
                <InputTextField
                    placeholder={SCHEDULE_ALIAS_PLACEHOLDER}
                    value={newScheduleAlias}
                    onChange={handleChangeTextField}
                    onFocus={clearPlaceholder}
                    onBlur={resetPlaceholder(SCHEDULE_ALIAS_PLACEHOLDER)}
                    autoFocus
                />
            </AuthTextFieldContainer>
            <ButtonContainer>
                <CancelButton onClick={() => setUpdateScheduleAliasDialogOpen(false)}>
                    취소
                </CancelButton>
                <UpdateScheduleAliasButton
                    onClick={handleClickUpdateScheduleAliasButton}
                >
                    수정완료
                </UpdateScheduleAliasButton>
            </ButtonContainer>
        </UpdateScheduleAliasContainer>
    );
};

const UpdateScheduleAliasContainer = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 100000;
  position: absolute;
  background: ${(props) => props.theme.palette.primitives.white};
  width: 273px;
  height: 149px;
  left: -15px;
  filter: drop-shadow(0px 7px 20px rgba(0, 0, 0, 0.1));
  border-radius: 4px;
`;

const UpdateScheduleAliasTitle = styled.div`
  color: ${(props) => props.theme.palette.primitives.gray600};
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  margin: 20px 0 12px 12px;
`;

const AuthTextFieldContainer = styled.div`
  position: relative;
  height: 33px;
  padding: 0 12px;
`;

const RightText = styled.div`
  position: absolute;
  font-weight: 500;
  font-size: 11px;
  line-height: 13px;
  text-align: right;
  color: ${(props) => props.theme.palette.primitives.gray600};
  right: 24px;
  top: 50%;
  margin-top: -7px;
`;

const InputTextField = styled.input`
  width: 100%;
  color: ${(props) => props.theme.palette.primitives.gray900};
  border: 1px solid #e0e3e9;
  border-radius: 4px;
  background: #f5f6f9;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  padding: 8px 12px;
  box-sizing: border-box;
  outline: none;
  caret-color: ${theme.palette.primitives.yellow400};

  &::placeholder {
    color: ${(props) => props.theme.palette.primitives.gray300};
  }

  padding-right: 40px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: end;
  margin: 24px 12px 0;
`;

const CancelButton = styled.button`
  background: ${(props) => props.theme.palette.primitives.gray50};
  padding: 8px 12px;
  border: 0;
  border-radius: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: ${(props) => props.theme.palette.primitives.gray400};
  cursor: pointer;
`;

const UpdateScheduleAliasButton = styled.button`
  background: ${(props) => props.theme.palette.primitives.gray50};
  padding: 8px 12px;
  margin-left: 4px;
  border: 0;
  border-radius: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  text-align: right;
  color: ${(props) => props.theme.palette.primitives.gray600};
  cursor: pointer;
`;
